import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "./api";
import { ERROR_MESSAGES } from "./errorCatalog.generated";
import { toast } from "./toast";

// The text a capability denial gets. A missing permission is not a fault the user
// can fix by retrying, so it must not read like a failure.
const NO_PERMISSION =
  "You don't have permission for that. Ask an owner or manager to grant it.";

// A bare capability denial: authkit's Require middleware answers 403 with
// `{"error":"forbidden"}`, and `forbidden` is not a code the generated catalog
// knows (it comes from the auth library, not httperr's registry), so the wire
// word itself — the literal string "forbidden" — was reaching the toast.
//
// Matched by "403 whose code the catalog cannot explain" rather than by the code
// string, so every 403 that DOES carry a specific meaning keeps its own text:
// `branch_forbidden` still says you can only file to a branch you belong to, and
// a step-up challenge is filtered out before this by `isStepUp`.
function isBareForbidden(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.isForbidden &&
    (!err.code || !(err.code in ERROR_MESSAGES))
  );
}

function errorMessage(err: unknown): string {
  if (isBareForbidden(err)) return NO_PERMISSION;
  // Server errors carry a human-readable message. Everything else (network
  // failures, JS/TypeErrors, TanStack Query internals like "data is undefined")
  // carries a technical message, so show a friendly generic instead.
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

// A step-up 403 ({code:"step_up_required"}) is handled by the PIN flow, not an
// error; never toast it.
function isStepUp(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { code?: string }).code === "step_up_required"
  );
}

// Shared TanStack Query client. Global handlers surface every API error as a
// toast (top-centre) and emit a success toast when a mutation declares
// meta.successMessage. Don't retry auth failures and keep data fresh-ish.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      // Queries: skip expected/handled cases: 404 (e.g. a deleted owner we render
      // as "Deleted customer"), 401 (redirected to login), and step-up.
      if (err instanceof ApiError && (err.status === 404 || err.isUnauthorized)) return;
      if (isStepUp(err)) return;
      // A bare capability denial on a QUERY is not feedback. The user did not ask
      // for the request - the page fired it on arrival - so a toast tells them off
      // for opening a screen. Pages render the denial in place instead
      // (`forbidden(error)` + <NoAccess>), which says which screen and stays put.
      //
      // Three of these landed at once on Settings for a mechanic, and the sidebar's
      // entitlements read put a fourth on every page in the app. Suppressing it here
      // is the floor, not the fix: each of those was also a route gated on the wrong
      // capability, and a page that fetches what its user may not read is still a
      // bug. This only stops it being a bug that shouts.
      //
      // Deliberately BARE denials only. A 403 that carries a specific code still
      // toasts: `plan_limit_reached` and `branch_forbidden` are answers to something
      // the user did, and they say something a NoAccess panel cannot.
      if (isBareForbidden(err)) return;
      toast.error(errorMessage(err));
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (isStepUp(err)) return;
      if (err instanceof ApiError && err.isUnauthorized) return;
      toast.error(errorMessage(err));
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const msg = mutation.meta?.successMessage;
      if (typeof msg === "string" && msg) toast.success(msg);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => {
        if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});
